import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, Wifi } from 'lucide-react';
import { flushQueue, getMetrics, subscribe, type QueueMetrics } from './posQueue';

/**
 * Compact connectivity + sync indicator for the TopBar.
 *
 * Three visual states:
 *   - Online + queue empty       → small green Wi-Fi pip (silent)
 *   - Online + queue has rows    → amber pill with pending count + "Sync now"
 *   - Offline                    → red CloudOff pill ("Offline · {n} pending")
 */
export default function OfflineBadge() {
  const [m, setM] = useState<QueueMetrics>({ pending: 0, lastSyncAt: null, online: true });
  const [flushing, setFlushing] = useState(false);

  useEffect(() => subscribe(setM), []);
  useEffect(() => {
    void getMetrics().then(setM);
  }, []);

  if (m.online && m.pending === 0) {
    return (
      <span
        title={`Online${m.lastSyncAt ? ` · last sync ${m.lastSyncAt.slice(11, 16)} UTC` : ''}`}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-success"
        aria-label="Online"
      >
        <Wifi size={13} />
      </span>
    );
  }

  const offline = !m.online;
  const cls = offline
    ? 'border-danger/40 bg-danger/10 text-danger'
    : 'border-warning/40 bg-warning/10 text-warning';

  return (
    <button
      type="button"
      onClick={async () => {
        if (offline || flushing) return;
        setFlushing(true);
        try { await flushQueue(); } finally { setFlushing(false); }
      }}
      title={
        offline
          ? `Offline · ${m.pending} sale(s) waiting`
          : `${m.pending} pending · click to retry sync now`
      }
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2 text-[11px] ${cls} ${
        offline ? '' : 'hover:opacity-80'
      }`}
      aria-label={offline ? 'Offline' : 'Pending sync'}
    >
      {offline ? (
        <CloudOff size={12} />
      ) : (
        <RefreshCw size={12} className={flushing ? 'animate-spin' : ''} />
      )}
      <span className="font-medium">
        {offline ? 'Offline' : flushing ? 'Syncing…' : 'Pending'}
      </span>
      {m.pending > 0 && <span className="tabular-nums">· {m.pending}</span>}
    </button>
  );
}
